import { ampli } from '@/ampli';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { z } from 'zod';

import { useFiatDisplay } from '@/hooks/useFiatDisplay';

import { FilterIcon } from '@/ui/Icons/FilterIcon';
import { XIcon } from '@/ui/Icons/XIcon';

import { useIsDocumentReady } from '../../../hooks/useIsDocumentReady';
import { buttons } from '../../../styles/buttons';
import { inputStyles } from '../../../styles/input';
import { fontStyles } from '../../../styles/typography';
import { Modal } from '../../../ui/Modal';
import { classNames } from '../../../utils/classnames';
import {
  DiscoveryFilters,
  DiscoveryNumericFilter,
  DiscoveryParametersSerialized,
  DiscoveryPreset,
  DiscoveryTimeframe,
  presets,
  zSerializedDiscoveryParameters,
} from '../../../utils/discovery/types';
import {
  deserializeDiscoveryState,
  discoveryPresets,
  serializeParams,
} from '../../../utils/discovery/utils';
import { DiscoveryTable } from '../DiscoveryTable';
import { CategoryFilters } from './CategoryFilters';
import { DiscoveryCategories } from './DiscoveryCategories';

type State = {
  filters: DiscoveryFilters;
  timeframe: DiscoveryTimeframe;
  sortBy: DiscoveryParametersSerialized['sortBy'];
  sortDirection: DiscoveryParametersSerialized['sortDirection'];
  selectedPreset: DiscoveryPreset | null;
  page: number;
  searchQuery: string;
};

type ActionTypes =
  | {
      type: 'set-filter';
      payload: DiscoveryFilters;
    }
  | {
      type: 'set-timeframe';
      payload: DiscoveryTimeframe;
    }
  | {
      type: 'set-preset';
      payload: DiscoveryPreset | null;
    }
  | {
      type: 'set-sorting';
      payload: {
        sortBy: DiscoveryParametersSerialized['sortBy'];
        sortDirection: DiscoveryParametersSerialized['sortDirection'];
      };
    }
  | {
      type: 'set-page';
      payload: number;
    }
  | {
      type: 'set-search-query';
      payload: string;
    };

const SELECT_OVERRIDES = classNames(
  'hover:bg-token-purple-400',
  'active:bg-token-purple-400',
  'focus-visible:outline-[3px]',
  'focus-visible:outline-token-purple-600',
);

function reducer(state: State, action: ActionTypes) {
  switch (action.type) {
    case 'set-filter':
      return { ...state, filters: action.payload, page: 1 };
    case 'set-timeframe':
      // Timeframe now only applies to trending preset
      if (state.selectedPreset === 'trending') {
        return { ...state, timeframe: action.payload };
      }
      // Ignore timeframe changes when not on trending preset
      return state;
    case 'set-sorting':
      // Reset preset when sorting tokens if active preset is "trending" because these
      // tokens are retrieved by sorting them by the "trendingScore" attribute
      const selectedPreset = state.selectedPreset === 'trending' ? null : state.selectedPreset;

      return { ...state, ...action.payload, selectedPreset, page: 1 };
    case 'set-preset':
      if (action.payload === null) {
        return { ...state, selectedPreset: null, searchQuery: '' };
      }

      // Use the current timeframe for trending preset and 24h for other presets
      const timeframe = action.payload === 'trending' ? state.timeframe : '24';
      const networks = state.filters.networks ?? [];

      // Reset preset-specific filters when changing preset
      const { launchpads, currencies, issuers, backings } =
        state.selectedPreset !== action.payload ? {} : state.filters;

      const otherFilters = { networks, launchpads, currencies, issuers, backings };

      return {
        ...state,
        ...discoveryPresets[action.payload],
        selectedPreset: action.payload,
        timeframe,
        page: 1,
        filters: { ...state.filters, ...otherFilters },
        // Clear any active search when switching categories; the previous query is
        // no longer meaningful against the new result set.
        searchQuery: state.selectedPreset === action.payload ? state.searchQuery : '',
      };
    case 'set-page':
      return { ...state, page: action.payload };
    case 'set-search-query':
      return { ...state, searchQuery: action.payload, page: 1 };
  }
}

function FilterRow({
  label,
  filter = {},
  unit,
  fxRate = 1,
  onChange,
}: {
  label: React.ReactNode;
  filter: DiscoveryNumericFilter | undefined;
  unit: string;
  fxRate?: number;
  onChange: (newVal: DiscoveryNumericFilter) => void;
}) {
  const styles = inputStyles();
  // Ensure filter has a default object structure
  const safeFilter = filter || {};

  // Display values converted from internal USD to user's currency
  const displayMin = safeFilter.min != null ? Math.round(safeFilter.min * fxRate) : '';
  const displayMax = safeFilter.max != null ? Math.round(safeFilter.max * fxRate) : '';

  return (
    <>
      <span
        className={fontStyles({
          variant: 'LABEL_MD',
          class: ['col-span-1', 'flex', 'items-center'],
        })}
      >
        {label}
      </span>

      <div className={styles.root({ class: 'col-span-1' })}>
        <span className={styles.accessoryLeft({ class: 'text-token-gray-700' })}>Min</span>
        <input
          type="number"
          className={styles.input()}
          value={displayMin}
          onChange={(e) => {
            onChange({
              ...safeFilter,
              min: e.target.value ? Number(e.target.value) / fxRate : undefined,
            });
          }}
        />
        <span className={styles.accessoryRight()}>{unit}</span>
      </div>
      <div className={styles.root({ class: 'col-span-1' })}>
        <span className={styles.accessoryLeft({ class: 'text-token-gray-700' })}>Max</span>
        <input
          type="number"
          className={styles.input()}
          value={displayMax}
          onChange={(e) => {
            onChange({
              ...safeFilter,
              max: e.target.value ? Number(e.target.value) / fxRate : undefined,
            });
          }}
        />
        <span className={styles.accessoryRight()}>{unit}</span>
      </div>
    </>
  );
}

const defaultDiscoveryState = () => ({
  filters: { networks: [] },
  timeframe: '24',
  selectedPreset: 'trending',
  page: 1,
  ...discoveryPresets.trending,
  searchQuery: '',
});

function fetchStateFromUrl(searchParams: URLSearchParams, includePopular: boolean) {
  // Check if preset is specified in URL
  const presetParam = searchParams.get('preset');

  // Popular has no category button on pages that exclude it; fall back to the default view.
  if (presetParam === 'popular' && !includePopular) return defaultDiscoveryState();

  if (presetParam && presets.includes(presetParam as DiscoveryPreset)) {
    const timeframeParam = searchParams.get('timeframe');
    const networksParam = searchParams.get('networks');
    const pageParam = searchParams.get('page');
    const sortByParam = searchParams.get('sortBy');
    const sortDirectionParam = searchParams.get('sortDirection');
    const creatorParam = searchParams.get('creator');

    const presetConfig = discoveryPresets[presetParam as DiscoveryPreset];
    const networks = networksParam?.split(',').map(Number) ?? [];

    const arrayFilters = ['launchpads', 'currencies', 'issuers', 'backings'].reduce(
      (filters, filterName) => ({
        ...filters,
        [filterName]: searchParams.get(filterName)?.split(','),
      }),
      {},
    );

    return {
      filters: { networks, creator: creatorParam, ...arrayFilters },
      timeframe: timeframeParam || '24',
      selectedPreset: presetParam,
      page: pageParam ? parseInt(pageParam, 10) : 1,
      sortBy: sortByParam || presetConfig.sortBy,
      sortDirection: sortDirectionParam || presetConfig.sortDirection,
      searchQuery: '',
    };
  }

  // No preset or invalid preset - parse individual state
  const stateKeys = Object.keys(zSerializedDiscoveryParameters.shape);
  const stateKeysSubset = Array.from(searchParams.keys()).filter((key) => stateKeys.includes(key));

  if (stateKeysSubset.length === 0) return defaultDiscoveryState();

  // Parse and transform only the provided keys using the Zod schema so that
  // values like numbers, percentages, and networks are coerced correctly.
  const partialRawParams = stateKeysSubset.reduce(
    (acc, key) => {
      acc[key] = searchParams.get(key) ?? '';
      return acc;
    },
    {} as Record<string, unknown>,
  );

  const parsed = zSerializedDiscoveryParameters.partial().safeParse(partialRawParams);
  if (!parsed.success) return defaultDiscoveryState();

  const deserialized = deserializeDiscoveryState(
    parsed.data as z.infer<typeof zSerializedDiscoveryParameters>,
  );
  return { ...deserialized, selectedPreset: null, searchQuery: '' };
}

function getTransformedTime(
  filter: { min?: number | undefined; max?: number | undefined } | undefined,
  timeUnit: 'min' | 'hour',
) {
  if (timeUnit === 'min') return filter;
  if (timeUnit === 'hour') {
    return {
      min: filter?.min ? Number((filter.min / 60).toFixed(2)) : undefined,
      max: filter?.max ? Number((filter.max / 60).toFixed(2)) : undefined,
    };
  }
}

type DiscoverySectionProps = {
  showStocksAvailabilityBanner: boolean;
  /** The legacy homepage keeps the Popular category; the tokens page drops it. */
  includePopular: boolean;
};

export function DiscoverySection({
  showStocksAvailabilityBanner,
  includePopular,
}: DiscoverySectionProps) {
  const searchParams = useSearchParams();
  const router = useRouter();
  const { currencySymbol, fxRate } = useFiatDisplay();

  const [state, dispatch] = useReducer(
    reducer,
    fetchStateFromUrl(searchParams, includePopular) as State,
  );

  const [selectedTimeMode, setSelectedTimeMode] = useState<'min' | 'hour'>('min');

  // Track if we've synced from URL to prevent loops
  const hasInitializedFromUrl = useRef(false);

  // Sync state with URL params.
  // With ISR/getStaticProps, useSearchParams() can be empty on first hydration.
  // Read window.location.search directly on the first run to get the real URL,
  // then fall back to searchParams for subsequent updates.
  useEffect(() => {
    if (hasInitializedFromUrl.current && searchParams.size !== 0) return;
    hasInitializedFromUrl.current = true;

    const effectiveParams =
      typeof window !== 'undefined' && searchParams.size === 0
        ? new URLSearchParams(window.location.search)
        : searchParams;

    const newState = fetchStateFromUrl(effectiveParams, includePopular) as State;

    // Batch update all state from URL
    // Update preset first if different
    if (newState.selectedPreset !== state.selectedPreset) {
      dispatch({
        type: 'set-preset',
        payload: newState.selectedPreset,
      });
    }

    // Update filters if different
    if (JSON.stringify(newState.filters) !== JSON.stringify(state.filters)) {
      dispatch({
        type: 'set-filter',
        payload: newState.filters,
      });
    }

    // Update timeframe if different
    if (newState.timeframe !== state.timeframe) {
      dispatch({
        type: 'set-timeframe',
        payload: newState.timeframe,
      });
    }

    const presetIsChanging =
      newState.selectedPreset !== state.selectedPreset && newState.selectedPreset != null;
    const effectiveSortBy = presetIsChanging ? newState.sortBy : state.sortBy;
    const effectiveSortDirection = presetIsChanging
      ? newState.sortDirection
      : (state.sortDirection ?? 'desc');

    if (
      newState.sortBy !== effectiveSortBy ||
      (newState.sortDirection ?? 'desc') !== effectiveSortDirection
    ) {
      dispatch({
        type: 'set-sorting',
        payload: {
          sortBy: (newState.sortBy || 'marketCap') as DiscoveryParametersSerialized['sortBy'],
          sortDirection: (newState.sortDirection ||
            'desc') as DiscoveryParametersSerialized['sortDirection'],
        },
      });
    }

    // Update page LAST to ensure it's not overridden by other state changes
    // Use setTimeout to ensure it runs after the preset update
    if (newState.page !== state.page) {
      setTimeout(() => dispatch({ type: 'set-page', payload: newState.page }), 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const numSetFilters = useMemo(() => {
    const { networks, launchpads, creator, currencies, issuers, backings, ...rest } = state.filters;
    return Object.keys(rest).length;
  }, [state]);

  const [stagedFilters, setStagedFilters] = useState<DiscoveryFilters>(state.filters);

  const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false);

  const closeButtonRef = useRef<HTMLButtonElement>(null);

  const setSorting = (sorting: {
    sortBy: DiscoveryParametersSerialized['sortBy'];
    sortDirection: DiscoveryParametersSerialized['sortDirection'];
  }) => {
    dispatch({
      type: 'set-sorting',
      payload: sorting,
    });
  };

  const onCloseFiltersModal = () => {
    setIsFiltersModalOpen(false);
    ampli.homepageFilterApplied();
    dispatch({ type: 'set-filter', payload: stagedFilters });

    // Reset preset when applying filters on trending category
    const { networks, launchpads, ...rest } = stagedFilters;
    if (state.selectedPreset === 'trending' && Object.keys(rest).length > 0) {
      dispatch({ type: 'set-preset', payload: null });
    }
  };

  const handleCategoryChange = (category: DiscoveryPreset) => {
    if (category) {
      ampli.homepageCategoryClicked({ category });
    }
    dispatch({ type: 'set-preset', payload: category });
  };

  const onChangeCallback = (key: keyof DiscoveryFilters, value: DiscoveryNumericFilter) => {
    let newVal: DiscoveryNumericFilter | undefined = undefined;
    if (value.max !== undefined) {
      newVal = {
        max: value.max,
      };
    }
    if (value.min !== undefined) {
      if (newVal) {
        newVal.min = value.min;
      } else {
        newVal = {
          min: value.min,
        };
      }
    }
    if (newVal) {
      setStagedFilters({
        ...stagedFilters,
        [key]: newVal,
      });
    } else {
      delete stagedFilters[key];
      setStagedFilters({ ...stagedFilters });
    }
  };

  const onPageChange = (page: number) => {
    const isSamePage = page === state.page;

    dispatch({ type: 'set-page', payload: page });
    if (isSamePage) return;
    // scroll to #discovery-table-top
    const tableTop = document.getElementById('discovery-table-top');
    if (tableTop) {
      // scroll with a negative offset of 150px
      document.documentElement.scrollTo({
        top: tableTop.offsetTop - 150,
        behavior: 'smooth',
      });
    }
  };

  const handleFiltersChange = (filters: DiscoveryFilters) =>
    dispatch({ type: 'set-filter', payload: filters });

  // Track if this is the first render to avoid setting URL on initial load
  const isFirstRender = useRef(true);

  // Listen for back navigation and re-parse URL state
  useEffect(() => {
    const handler = (event: PopStateEvent) => {
      // Skip filter processing as user is navigating to another page (e.g. token page)
      if (event.state?.url?.split('/').length > 2) {
        return;
      }

      window.__discoveryNavigatingHistory = true;
      const newState = fetchStateFromUrl(
        new URLSearchParams(window.location.search),
        includePopular,
      ) as State;
      if (newState.selectedPreset) {
        dispatch({ type: 'set-preset', payload: newState.selectedPreset });
      } else {
        dispatch({ type: 'set-preset', payload: null });
      }
      dispatch({ type: 'set-filter', payload: newState.filters });
      dispatch({ type: 'set-timeframe', payload: newState.timeframe });
      // Sorting is derived from the preset for Trending; dispatching it would clear the preset.
      if (newState.selectedPreset !== 'trending') {
        dispatch({
          type: 'set-sorting',
          payload: {
            sortBy: (newState.sortBy || 'trendingScore') as DiscoveryParametersSerialized['sortBy'],
            sortDirection: (newState.sortDirection ||
              'desc') as DiscoveryParametersSerialized['sortDirection'],
          },
        });
      }
      if (newState.page && newState.page !== 1) {
        dispatch({ type: 'set-page', payload: newState.page });
      } else {
        dispatch({ type: 'set-page', payload: 1 });
      }
    };
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, [includePopular]);

  // Sync state with URL (push to history)
  useEffect(() => {
    // Skip the first render to avoid setting URL on initial load
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    // If we navigated via back/forward, don't push again
    if (window.__discoveryNavigatingHistory) {
      window.__discoveryNavigatingHistory = false;
      return;
    }

    // Serialize current state to URL parameters.
    // The in-memory search query is intentionally not reflected in the URL.
    const serializedParams = serializeParams({
      filters: state.filters,
      timeframe: state.timeframe,
      sortBy: state.sortBy,
      sortDirection: state.sortDirection || 'desc',
      page: state.page,
      preset: state.selectedPreset || undefined,
      pageDiscovery: false,
      searchQuery: undefined,
    });

    // Build new URLSearchParams
    const newSearchParams = new URLSearchParams();

    // If trending preset is selected, store it in the URL instead of individual filters
    if (state.selectedPreset === 'trending') {
      newSearchParams.set('preset', state.selectedPreset);

      // Still include non-default timeframe if changed
      if (state.timeframe !== '24') {
        newSearchParams.set('timeframe', state.timeframe);
      }

      // Still include networks when set
      if (state.filters.networks && state.filters.networks.length > 0) {
        newSearchParams.set('networks', state.filters.networks.join(','));
      }

      // Still include page if not 1
      if (state.page > 1) {
        newSearchParams.set('page', String(state.page));
      }
    } else {
      // Only add parameters that have meaningful values
      Object.entries(serializedParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          // Skip default values to keep URL clean
          if (key === 'sortDirection' && value === 'desc') return;
          if (key === 'page' && value === 1) return;
          if (key === 'timeframe' && value === '24') return;
          if (key === 'sortBy' && value === 'trendingScore') return;
          if (key === 'pageDiscovery') return;

          newSearchParams.set(key, String(value));
        }
      });
    }

    const nextQuery = newSearchParams.toString();
    const currentQuery = window.location.search.startsWith('?')
      ? window.location.search.slice(1)
      : window.location.search;
    if (currentQuery !== nextQuery) {
      const newUrl = `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ''}`;
      router.push(newUrl, { scroll: false });
    }
  }, [state, router]);

  const isDocumentReady = useIsDocumentReady();

  return (
    <div
      data-ready={isDocumentReady ? 'true' : 'false'}
      id="discovery-table-top"
      className={classNames('relative z-1 -mt-3.5 tablet:-mt-10')}
    >
      <div
        className={classNames(
          'flex flex-col gap-6 tablet:max-w-[min(calc(100vw-36px),1528px)]',
          'desktop:max-w-[min(calc(100vw-200px),1528px)] mx-auto mb-4 z-10',
          'pb-2',
        )}
      >
        <DiscoveryCategories
          activeCategory={state.selectedPreset}
          onChange={handleCategoryChange}
          includePopular={includePopular}
        />
        <div
          className={classNames(
            'flex flex-row tablet:justify-between overflow-auto no-scrollbar px-4.5 py-1 -my-1 tablet:px-1 tablet:-mx-1',
          )}
        >
          <CategoryFilters
            timeframe={state.timeframe}
            filters={state.filters}
            preset={state.selectedPreset}
            onFiltersChange={handleFiltersChange}
            onTimeframeChange={(timeframe) =>
              dispatch({ type: 'set-timeframe', payload: timeframe })
            }
          />
          <div className="flex gap-2 shrink-0 items-center">
            <div className="w-px h-5 bg-token-alphalight-300 tablet:hidden ml-2" />
            <button
              className={buttons({
                size: 'small',
                iconLeft: true,
                shape: 'rounded',
                color: numSetFilters > 0 ? 'lightPurple' : 'gray',
                class: numSetFilters > 0 ? SELECT_OVERRIDES : '',
              })}
              onClick={() => {
                setStagedFilters(state.filters);
                setIsFiltersModalOpen(true);
                closeButtonRef.current?.blur();
              }}
            >
              <FilterIcon
                className={classNames(
                  numSetFilters > 0 ? 'text-token-purple-1100' : 'text-token-gray-1600',
                )}
              />
              <span>Filter</span>
              {numSetFilters > 0 && (
                <span
                  className={fontStyles({
                    variant: 'LABEL_XS',
                    class: [
                      'text-token-purple-1100 leading-3.5 py-px px-[4.5px] rounded-full',
                      'size-4.5 border border-solid border-token-purple-600',
                      'flex items-center justify-center bg-token-purple-400',
                    ],
                  })}
                >
                  {numSetFilters}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
      <DiscoveryTable
        filters={state.filters}
        timeframe={state.selectedPreset === 'trending' ? state.timeframe : '24'}
        sortBy={state.sortBy}
        sortDirection={state.sortDirection}
        setSorting={setSorting}
        page={state.page}
        setPage={onPageChange}
        preset={state.selectedPreset || undefined}
        showStocksAvailabilityBanner={showStocksAvailabilityBanner}
        searchQuery={state.searchQuery}
        onSearchQueryChange={(query) => dispatch({ type: 'set-search-query', payload: query })}
      />
      <Modal variant="LARGE" isOpen={isFiltersModalOpen} onClose={onCloseFiltersModal}>
        <div className="flex justify-between w-full h-fit pt-3 pl-6 pr-3 items-center">
          <h5 className={fontStyles({ variant: 'H5' })}>Filter</h5>
          <button
            className={buttons({
              size: 'small',
              shape: 'rounded',
              color: 'transparent',
            })}
            onClick={onCloseFiltersModal}
            ref={closeButtonRef}
          >
            <XIcon className="text-token-gray-1600 w-4 h-4" />
          </button>
        </div>
        <div className="mt-6 px-6 pb-4">
          <div>
            <div className="grid grid-cols-[calc(25%-8px)_calc(37.5%-8px)_calc(37.5%-8px)] gap-x-4 gap-y-3">
              <span
                className={fontStyles({
                  variant: 'LABEL_XS',
                  class: [
                    'h-7.5',
                    'py-2',
                    'text-token-gray-800',
                    'leading-3.5',
                    'col-span-3',
                    '-mb-1',
                  ],
                })}
              >
                General
              </span>
              <FilterRow
                label="Liquidity"
                filter={stagedFilters.liquidity}
                unit={currencySymbol}
                fxRate={fxRate}
                onChange={(newVal) => {
                  onChangeCallback('liquidity', newVal);
                }}
              />
              <FilterRow
                label="Market Cap"
                filter={stagedFilters.marketCap}
                unit={currencySymbol}
                fxRate={fxRate}
                onChange={(newVal) => {
                  onChangeCallback('marketCap', newVal);
                }}
              />
              <FilterRow
                label={`Volume (${state.timeframe}h)`}
                filter={stagedFilters.volByTime}
                unit={currencySymbol}
                fxRate={fxRate}
                onChange={(newVal) => {
                  onChangeCallback('volByTime', newVal);
                }}
              />
              <FilterRow
                label={
                  <>
                    Age&nbsp;
                    <span>
                      (
                      <button
                        className="data-[active=false]:text-token-gray-700"
                        data-active={selectedTimeMode === 'min'}
                        onClick={() => setSelectedTimeMode('min')}
                      >
                        Min
                      </button>{' '}
                      ·{' '}
                      <button
                        className="data-[active=false]:text-token-gray-700"
                        data-active={selectedTimeMode === 'hour'}
                        onClick={() => setSelectedTimeMode('hour')}
                      >
                        Hours
                      </button>
                      )
                    </span>
                  </>
                }
                filter={getTransformedTime(stagedFilters.age, selectedTimeMode)}
                unit={selectedTimeMode === 'min' ? 'min' : 'hours'}
                onChange={(newVal) => {
                  if (selectedTimeMode === 'min') {
                    onChangeCallback('age', newVal);
                  } else {
                    onChangeCallback('age', {
                      min: newVal.min ? Math.round(newVal.min * 60) : undefined,
                      max: newVal.max ? Math.round(newVal.max * 60) : undefined,
                    });
                  }
                }}
              />
              <FilterRow
                label="Holders"
                filter={stagedFilters.numHolders}
                unit="#"
                onChange={(newVal) => {
                  onChangeCallback('numHolders', newVal);
                }}
              />
              <span
                className={fontStyles({
                  variant: 'LABEL_XS',
                  class: [
                    'h-7.5',
                    'py-2',
                    'text-token-gray-800',
                    'leading-3.5',
                    'col-span-3',
                    '-mb-1',
                  ],
                })}
              >
                Price Change
              </span>
              <FilterRow
                label="5m %"
                filter={stagedFilters.change5m}
                unit="%"
                onChange={(newVal) => {
                  onChangeCallback('change5m', newVal);
                }}
              />
              <FilterRow
                label="1h %"
                filter={stagedFilters.change1}
                unit="%"
                onChange={(newVal) => {
                  onChangeCallback('change1', newVal);
                }}
              />
              <FilterRow
                label="4h %"
                filter={stagedFilters.change4}
                unit="%"
                onChange={(newVal) => {
                  onChangeCallback('change4', newVal);
                }}
              />
              <FilterRow
                label="12h %"
                filter={stagedFilters.change12}
                unit="%"
                onChange={(newVal) => {
                  onChangeCallback('change12', newVal);
                }}
              />
              <FilterRow
                label="24h %"
                filter={stagedFilters.change24}
                unit="%"
                onChange={(newVal) => {
                  onChangeCallback('change24', newVal);
                }}
              />
            </div>
          </div>
        </div>
        <div className="h-[84px] p-6 border-t border-solid border-t-token-gray-200 flex w-full justify-between">
          <button
            onClick={() => {
              ampli.homepageFilterReset();
              setStagedFilters({ networks: [] });
            }}
            className={buttons({
              size: 'small',
              shape: 'rounded',
              color: 'transparent',
            })}
          >
            Reset defaults
          </button>
          <button
            onClick={onCloseFiltersModal}
            className={buttons({
              size: 'small',
              shape: 'rounded',
              color: 'purple',
            })}
          >
            Apply filters
          </button>
        </div>
      </Modal>
    </div>
  );
}
